import type { Logger } from '@mimir/common/types/logger';
import type { DecodedVideoFrame } from '@mimir/cutter/frameProvider/demuxAndDecode/demuxAndDecodeFrameProvider';
import type { VideoDecoder as VideoDecoderInterface, VideoPacket, VideoCodec } from '@mimir/cutter/frameProvider/demuxAndDecode/demuxAndDecodeFrameProvider';
import { approximateFractionValue, frac } from '@mimir/common/util/integerMath';
import { exceptionToString } from '@mimir/common/util/exception';
import { Guard } from '@mimir/common/async/async';
import type { PixelFormat } from '@mimir/common/transcoding/types';
import { abortify } from '@mimir/common/util/abortify';
import type { WebGlTextureWrapper, WebGlWrapper } from '@mimir/browser/gldraw/webGlWrapper';
import { WebGlTextureBuffer } from '@mimir/browser/gldraw/webGlTextureBuffer';

export interface WebVideoDecoderParameters {
  logger: Logger,
  codec: VideoCodec,
  webGlWrapper: WebGlWrapper,
}

export function createWebVideoDecoder(parameters: WebVideoDecoderParameters): VideoDecoderInterface {
  return createRecreatingVideoDecoder(parameters);
}

/// -----


const MAX_DECODE_QUEUE_SIZE : number = 5;


export function webApiVideoPixelFormatToPixelFormat(vpf: VideoPixelFormat | null): PixelFormat {
  switch (vpf) {
    case 'I420': return 'yuv420p';
    case 'RGBA': return 'rgba';
    case 'BGRA': return 'bgra';
    case 'BGRX': return 'bgr0';
    case 'NV12': return 'nv12';
    default:
      throw new Error(`Unsupported pixel format: ${vpf}`);
  }
}

/**
 *  It seems that it is not safe to reuse the same VideoDecoder instance by
 *  using reset. Sometimes the reset does not take effect and you end up
 *  getting the last packet from the previous run and nothing else. Because
 *  of this we wrap the inner decoder in this wrapper decoder that recreates
 *  it on reset(). If the browsers ever get their shit together we can remove
 *  this workaround.
 */
function createRecreatingVideoDecoder(parameters: WebVideoDecoderParameters): VideoDecoderInterface {
  let closed = false;
  let inner : VideoDecoderInterface | null = null;

  const ensureInner = (): VideoDecoderInterface => {
    if (!inner) {
      inner = createWebVideoDecoderInternal(parameters);
    }
    return inner;
  };

  return {
    close(): void {
      this.reset();
      closed = true;
    },
    async push(packet: VideoPacket, abortSignal: AbortSignal): Promise<void> {
      if (closed) {
        throw new Error('VideoDecoder is closed');
      }
      await ensureInner().push(packet, abortSignal);
    },
    async poll(abortSignal: AbortSignal): Promise<DecodedVideoFrame | null> {
      if (closed) {
        throw new Error('VideoDecoder is closed');
      }
      return await ensureInner().poll(abortSignal);
    },
    async flush(abortSignal: AbortSignal): Promise<void> {
      if (closed) {
        throw new Error('VideoDecoder is closed');
      }
      await ensureInner().flush(abortSignal);
    },
    reset(): void {
      if (closed) {
        throw new Error('VideoDecoder is closed');
      }
      if (inner !== null) {
        inner.close();
        inner = null;
      }
    },
  };
}


function createWebVideoDecoderInternal(parameters: WebVideoDecoderParameters): VideoDecoderInterface {
  const { logger, codec, webGlWrapper } = parameters;

  const dequeue = new Guard();

  const pendingVideoFrames : VideoFrame[] = [];

  const videoDecoder =
    new VideoDecoder({
      error: (error) => {
        logger.error(`WebVideoDecoder: error=${exceptionToString(error)} (codec=${codec})`);
      },
      output: (videoFrame: VideoFrame) => {
        pendingVideoFrames.push(videoFrame);
      },
    });

  videoDecoder.ondequeue = () => { dequeue.notify(); };

  let isConfigured = false;

  const pendingKeyFrames = new Set();

  return {
    reset(): void {
      for (const videoFrame of pendingVideoFrames.splice(0)) {
        videoFrame.close();
      }
      videoDecoder.reset();
      pendingKeyFrames.clear();
      isConfigured = false;
    },
    close(): void {
      this.reset();
      videoDecoder.close();
    },

    async push(packet: VideoPacket, abortSignal: AbortSignal): Promise<void> {
      if (!isConfigured) {
        videoDecoder.configure({
          codec: packet.codecString,
          description: packet.description,
          optimizeForLatency: true,
          // Chrome fails when trying to decode interlaced video with hardware acceleration
          hardwareAcceleration: packet.progressive ? undefined : 'prefer-software',
        });
        isConfigured = true;
      }

      while (videoDecoder.decodeQueueSize > MAX_DECODE_QUEUE_SIZE) {
        await abortify(abortSignal, () => dequeue.wait());
      }

      const timestampInMicroSeconds =
        Math.round(approximateFractionValue(packet.pts) * 1_000_000);

      if (packet.keyFrame) {
        pendingKeyFrames.add(timestampInMicroSeconds);
      }

      const chunk =
        new EncodedVideoChunk({
          data: packet.payload,
          timestamp: timestampInMicroSeconds,
          type: packet.keyFrame ? 'key' : 'delta',
        });

      videoDecoder.decode(chunk);
    },

    async poll(_abortSignal: AbortSignal): Promise<DecodedVideoFrame | null> {
      const [videoFrame] = pendingVideoFrames.splice(0, 1);
      if (videoFrame === undefined) {
        return null;
      }

      const timestamp = frac(videoFrame.timestamp, 1_000_000);
      if (videoFrame.visibleRect === null) {
        throw new Error('You got rected');
      }

      const width = videoFrame.visibleRect.width;
      const height = videoFrame.visibleRect.height;

      const keyFrame = pendingKeyFrames.delete(videoFrame.timestamp);

      const buffer = new WebGlTextureBuffer(convertVideoFrameToTexture(webGlWrapper, videoFrame));

      return {
        pts: timestamp,
        keyFrame,
        info: {
          resolution: { width, height },
          fieldOrder: 'progressive',
          pixelFormat: 'rgba',
        },
        buffer,
      };
    },

    async flush(abortSignal: AbortSignal): Promise<void> {
      if (!isConfigured) {
        return;
      }
      await abortify(abortSignal, () => videoDecoder.flush());
      while (videoDecoder.decodeQueueSize > MAX_DECODE_QUEUE_SIZE) {
        await abortify(abortSignal, () => dequeue.wait());
      }
    }
  };
}

function convertVideoFrameToTexture(
  webGlWrapper: WebGlWrapper,
  videoFrame: VideoFrame,
): WebGlTextureWrapper {
  const vertexShaderSource = `#version 300 es

  out vec2 uv;

  void main() {
    vec2 positions[6] = vec2[](
      vec2(-1.0, -1.0),
      vec2(1.0, -1.0),
      vec2(1.0, 1.0),
      vec2(-1.0, -1.0),
      vec2(1.0, 1.0),
      vec2(-1.0, 1.0)
    );
    vec2 uvs[6] = vec2[](
      vec2(0.0, 0.0),
      vec2(1.0, 0.0),
      vec2(1.0, 1.0),
      vec2(0.0, 0.0),
      vec2(1.0, 1.0),
      vec2(0.0, 1.0)
    );
    gl_Position = vec4(positions[gl_VertexID], vec2(0.0, 1.0));
    uv = uvs[gl_VertexID];
  }`;

  const fragmentShaderSource = `#version 300 es
    precision mediump float;

    uniform sampler2D srgb;

    in vec2 uv;
    out vec4 color;

    void main() {
      vec4 srgb_sample = texture(srgb, uv);
      color = vec4(pow(srgb_sample.rgb, vec3(2.2)), srgb_sample.a);
    }
  `;

  const videoFrameTexture =
    webGlWrapper.getTexture(
      'rgba8',
      videoFrame.displayWidth,
      videoFrame.displayHeight,
      {
        sourceType: 'texImageSource',
        texImageSource: videoFrame,
      }
    );


  const program = webGlWrapper.getLinkedProgram(vertexShaderSource, fragmentShaderSource);
  const framebuffer = webGlWrapper.getFrameBuffer(videoFrameTexture.width, videoFrameTexture.height);

  const gl = webGlWrapper.getGlContext();

  program.use(gl);

  const srgbLocation = program.getUniformLocation(gl, 'srgb');
  gl.uniform1i(srgbLocation, 0);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, videoFrameTexture.texture);

  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, framebuffer.frameBuffer);
  gl.viewport(0, 0, videoFrameTexture.width, videoFrameTexture.height);

  gl.drawArrays(gl.TRIANGLES, 0, 6);

  gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, null);

  videoFrameTexture.close();

  webGlWrapper.closeVideoFrame(videoFrame);

  return framebuffer.toTexture();
}

